import { useState } from 'react';
import { ROBOT_LEVELS } from '@strategy4kids/protocol';
import type { RobotLevel } from '@strategy4kids/protocol';
import { Button, Icon, Input, type IconName } from '@strategy4kids/ui';
import { levelLabel } from '../edition/index.js';
import { useT } from '../i18n/useT.js';
import { usePlayerLookStore, type BuddyId } from '../state/player-look-store.js';

const AVATARS: readonly IconName[] = ['Bot', 'Cat', 'Dog', 'Bird'];

const AVATAR_LABEL: Partial<Record<IconName, string>> = {
  Bot: 'Robot buddy',
  Cat: 'Cat buddy',
  Dog: 'Dog buddy',
  Bird: 'Bird buddy',
};

const GOALS = [
  { id: 'piece-power', label: 'Learn the pieces', blurb: 'How much is each piece worth?' },
  { id: 'moves', label: 'How pieces move', blurb: 'Knights hop, bishops slide!' },
  { id: 'rules', label: 'The rules', blurb: 'Check, checkmate and more.' },
  { id: 'objectives', label: 'Winning habits', blurb: 'Center control, king safety.' },
];

const STEPS = ['Hello!', 'Buddy', 'Robot', 'Goal', 'Board', 'Go!'];

/** The picture for each step (public/art/, generated). */
const STEP_ART = ['step-hello', 'step-buddy', 'step-robot', 'step-goal', 'step-board', 'step-go'] as const;

/** What each step's picture shows (a non-reader reads the pictures, so they are described, not hidden). */
const STEP_ALT = ['A friendly robot waving hello', 'Buddies to choose from', 'Robots ready to play', 'A path to a goal', 'A glowing board', 'A robot and a cat celebrating'] as const;

/** Shared classes for the selectable option rows (avatar/level/goal steps). */
const OPTION_BASE =
  'rounded-2xl border-2 px-5 py-4 text-left text-lg font-bold transition-colors lg:text-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const OPTION_ACTIVE = 'border-accent bg-accent-soft text-fg';
const OPTION_IDLE = 'border-border bg-surface text-fg-muted hover:border-accent/40 hover:text-fg';

export interface OnboardingWizardProps {
  readonly onDone: () => void;
  readonly initialLevel?: RobotLevel;
  readonly onLevel?: (level: RobotLevel) => void;
}

// Onboarding defaults are guarded at module init so component bodies stay
// non-null-assertion-free. The wizard is unreachable if any of these fire.
function firstRobotLevel(): RobotLevel {
  const first = ROBOT_LEVELS[0];
  if (!first) throw new Error('@strategy4kids/protocol: ROBOT_LEVELS is empty');
  return first;
}
function firstAvatar(): IconName {
  const first = AVATARS[0];
  if (!first) throw new Error('AVATARS is empty');
  return first;
}
function firstGoal(): (typeof GOALS)[number] {
  const first = GOALS[0];
  if (!first) throw new Error('GOALS is empty');
  return first;
}

const BOARD_PREVIEW_TILES = Array.from({ length: 16 }, (_, i) => ({ id: `r${Math.floor(i / 4)}c${i % 4}`, highlight: i % 5 === 0 }));

export function OnboardingWizard({ onDone, initialLevel = firstRobotLevel(), onLevel }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<IconName>(firstAvatar());
  const [goal, setGoal] = useState(firstGoal());
  const [level, setLevel] = useState<RobotLevel>(initialLevel);

  const { t } = useT('common');

  const isLast = step === STEPS.length - 1;
  /** Remember who sits at the table (name and buddy) before leaving, whether finished or skipped. */
  const finish = () => {
    usePlayerLookStore.getState().setLook({ name, buddy: avatar as BuddyId });
    onDone();
  };
  const pickLevel = (l: RobotLevel) => {
    setLevel(l);
    onLevel?.(l);
  };

  // bg-bg: semantic token flips dark automatically. The old
  // bg-cream dark:bg-ink pair inverted — in .dark, `ink` becomes the
  // light text color, so "dark mode" rendered light (audit F9).
  return (
    <main
      className="relative isolate flex min-h-dvh items-center justify-center p-3 sm:p-6 lg:p-10"
      data-testid="onboarding-wizard"
      data-appdom-scope="onboarding"
    >
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 bg-bg">
        <img src="/art/hero-home.webp" alt="" className="size-full object-cover opacity-70 dark:opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-bg/10 to-bg/60" />
      </div>
      <div className="grid w-full min-w-0 max-w-6xl grid-cols-1 overflow-hidden rounded-3xl border border-border bg-surface/90 shadow-2xl backdrop-blur-md lg:min-h-[36rem] lg:grid-cols-[1fr_1.15fr] xl:max-w-[88rem] lg:min-h-[min(46rem,82dvh)]">
        <div className="relative h-40 overflow-hidden bg-accent-soft sm:h-56 lg:h-auto" data-testid="wizard-art">
          <img
            key={STEP_ART[step]}
            src={`/art/${STEP_ART[step]}.webp`}
            alt={STEP_ALT[step]}
            className="absolute inset-0 size-full object-cover"
          />
        </div>
        <div className="flex min-w-0 flex-col p-5 sm:p-10 lg:p-14">
        <ol className="mb-8 flex gap-1.5" data-testid="wizard-steps" aria-label="Onboarding progress">
          {STEPS.map((label, i) => (
            <li
              key={label}
              aria-current={i === step ? 'step' : undefined}
              className={`h-2 flex-1 rounded-full transition-colors ${i <= step ? 'bg-accent' : 'bg-fg/10'}`}
            />
          ))}
        </ol>

        {step === 0 && (
          <section data-testid="wizard-step-name">
            <h1 className="font-display text-h1 font-bold text-fg">
              Hi there! What should we call you?
            </h1>
            <Input
              data-testid="wizard-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Type your name"
              className="mt-6 h-14 text-xl font-bold lg:h-16 lg:text-2xl"
            />
          </section>
        )}

        {step === 1 && (
          <section data-testid="wizard-step-avatar">
            <h1 className="font-display text-h1 font-bold text-fg">Pick your buddy</h1>
            <div className="mt-6 grid grid-cols-4 gap-2 sm:gap-4">
              {AVATARS.map((a) => (
                <button
                  key={a}
                  type="button"
                  data-testid={`avatar-${a}`}
                  aria-label={AVATAR_LABEL[a]}
                  aria-pressed={avatar === a}
                  onClick={() => setAvatar(a)}
                  className={`flex aspect-square w-full max-w-28 items-center justify-center rounded-2xl border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    avatar === a
                      ? `${OPTION_ACTIVE} ring-2 ring-accent`
                      : `${OPTION_IDLE} hover:bg-accent-soft/50`
                  }`}
                >
                  <Icon name={a} size="lg" className="scale-150 text-accent" label={AVATAR_LABEL[a]} />
                </button>
              ))}
            </div>
          </section>
        )}

        {step === 2 && (
          <section data-testid="wizard-step-level">
            <h1 className="font-display text-h1 font-bold text-fg">
              How hard should the robot be?
            </h1>
            <div className="mt-4 flex flex-col gap-2">
              {ROBOT_LEVELS.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`level-${l.id}`}
                  onClick={() => pickLevel(l)}
                  className={`${OPTION_BASE} ${level.id === l.id ? OPTION_ACTIVE : OPTION_IDLE}`}
                >
                  {levelLabel(l)}
                </button>
              ))}
            </div>
          </section>
        )}

        {step === 3 && (
          <section data-testid="wizard-step-goal">
            <h1 className="font-display text-h1 font-bold text-fg">
              What do you want to learn first?
            </h1>
            <div className="mt-4 flex flex-col gap-2">
              {GOALS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  data-testid={`goal-${g.id}`}
                  onClick={() => setGoal(g)}
                  className={`${OPTION_BASE} ${goal.id === g.id ? OPTION_ACTIVE : OPTION_IDLE}`}
                >
                  <span className="block">{g.label}</span>
                  <span className="block text-sm font-normal text-fg-muted">{g.blurb}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {step === 4 && (
          <section data-testid="wizard-step-board">
            <h1 className="font-display text-h1 font-bold text-fg">The board lights up!</h1>
            <p className="mt-3 text-lg text-fg-muted lg:text-xl">
              In lessons, the board shows where pieces are strong. Watch the colors!
            </p>
            <div className="mx-auto mt-4 grid w-full max-w-[14rem] grid-cols-4 gap-1 rounded-2xl sm:max-w-xs lg:max-w-sm border-2 border-sun/40 bg-white p-2 dark:border-sun/30 dark:bg-surface-raised">
              {/* Decorative board preview: 16 static tiles. Keys are the tile's row/column, unique (a colour key repeated). */}
              {BOARD_PREVIEW_TILES.map((tile) => (
                <div key={tile.id} className={`aspect-square rounded-md ${tile.highlight ? 'bg-sky/60' : 'bg-sun/30'}`} />
              ))}
            </div>
          </section>
        )}

        {step === 5 && (
          <section data-testid="wizard-step-go">
            <h1 className="font-display text-h1 font-bold text-fg">
              You’re all set{name ? `, ${name}` : ''}!
            </h1>
            <p className="mt-3 text-lg text-fg-muted lg:text-xl">
              Pick a lesson and start learning — the robot will meet you in the game.
            </p>
          </section>
        )}

        {/* Footer action row, inside the card: Back (ghost) pinned left,
            Skip + Next/Start pinned right. The old pair sat loose at the
            card edge and the primary clipped the border (audit F1). */}
        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-8 sm:pt-10">
          {step > 0 ? (
            <Button
              variant="ghost"
              size="lg"
              data-testid="wizard-back"
              data-appdom-id="wizard-back"
              data-appdom-type="Button"
              onClick={() => setStep(step - 1)}
            >
              {t('back')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="lg" data-testid="wizard-skip" onClick={finish}>
              {t('skip')}
            </Button>
            {!isLast ? (
              <Button
                size="lg"
                data-testid="wizard-next"
                data-appdom-id="wizard-next"
                data-appdom-type="Button"
                onClick={() => setStep(step + 1)}
              >
                {t('next')}
              </Button>
            ) : (
              <Button size="lg" data-testid="wizard-start" onClick={finish}>
                Start learning
              </Button>
            )}
          </div>
        </div>
        </div>
      </div>
    </main>
  );
}
